import React from 'react';
import Card, { CardContent } from '../../components/ui/Card';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
            <p className="text-sm text-gray-600 mb-8">Last Updated: {new Date().toLocaleDateString()}</p>

            <div className="prose prose-gray max-w-none">
              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">1. Introduction</h2>
              <p className="text-gray-700 mb-4">
                Welcome to Patreon Clone ("we," "our," or "us"). We are committed to protecting your personal information 
                and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                information when you use our platform.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.1 Personal Information</h3>
              <p className="text-gray-700 mb-4">We collect the following personal information:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Account Information:</strong> Name, email address, username, password (encrypted)</li>
                <li><strong>Profile Information:</strong> Profile picture, bio, social media links</li>
                <li><strong>Payment Information:</strong> Credit card details (processed securely through Stripe), billing address</li>
                <li><strong>Identity Verification:</strong> For creators, government-issued ID and tax information</li>
                <li><strong>Communication Data:</strong> Messages, comments, posts you create</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">2.2 Automatically Collected Information</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Device Information:</strong> IP address, browser type, operating system</li>
                <li><strong>Usage Data:</strong> Pages visited, features used, time spent on platform</li>
                <li><strong>Cookies and Tracking:</strong> Session cookies, preference cookies, analytics cookies</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">3. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">We use your information for the following purposes:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>To provide and maintain our services</li>
                <li>To process payments and subscriptions</li>
                <li>To communicate with you about your account and transactions</li>
                <li>To send you updates, newsletters, and marketing materials (with consent)</li>
                <li>To prevent fraud and ensure platform security</li>
                <li>To comply with legal obligations</li>
                <li>To improve our services through analytics</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">4. Information Sharing and Disclosure</h2>
              <p className="text-gray-700 mb-4">We may share your information with:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Payment Processors:</strong> Stripe for payment processing</li>
                <li><strong>Service Providers:</strong> Email delivery, analytics, hosting services</li>
                <li><strong>Creators:</strong> Your patron status and support level with creators you support</li>
                <li><strong>Legal Authorities:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">5. Data Security</h2>
              <p className="text-gray-700 mb-4">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Encryption of data in transit (SSL/TLS) and at rest</li>
                <li>Regular security audits and penetration testing</li>
                <li>Access controls and authentication requirements</li>
                <li>Secure payment processing through PCI-DSS compliant providers</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">6. Your Rights and Choices</h2>
              <p className="text-gray-700 mb-4">You have the following rights:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request deletion of your account and data</li>
                <li><strong>Data Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                <li><strong>Cookie Control:</strong> Manage cookie preferences in your browser</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">7. Data Retention</h2>
              <p className="text-gray-700 mb-4">
                We retain your information for as long as necessary to provide our services and comply with legal 
                obligations. Deleted accounts are anonymized after 30 days, though some data may be retained for 
                legal and accounting purposes.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">8. International Data Transfers</h2>
              <p className="text-gray-700 mb-4">
                Your information may be transferred to and processed in countries outside your residence. We ensure 
                adequate protection through standard contractual clauses and compliance with applicable data protection laws.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">9. Children's Privacy</h2>
              <p className="text-gray-700 mb-4">
                Our platform is not intended for users under 18 years of age. We do not knowingly collect personal 
                information from children. If we discover we have collected information from a child, we will promptly delete it.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">10. Changes to This Policy</h2>
              <p className="text-gray-700 mb-4">
                We may update this Privacy Policy from time to time. We will notify you of significant changes via 
                email or through a prominent notice on our platform. Continued use of our services after changes 
                constitutes acceptance of the updated policy.
              </p>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">11. Contact Us</h2>
              <p className="text-gray-700 mb-4">
                If you have questions or concerns about this Privacy Policy, please contact us at:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p className="text-gray-700"><strong>Email:</strong> privacy@patreonclone.com</p>
                <p className="text-gray-700"><strong>Address:</strong> [Your Company Address]</p>
                <p className="text-gray-700"><strong>Data Protection Officer:</strong> dpo@patreonclone.com</p>
              </div>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">12. GDPR Compliance (EU Users)</h2>
              <p className="text-gray-700 mb-4">
                For users in the European Union, we comply with GDPR requirements:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Legal basis for processing: Consent, contract performance, legal obligation</li>
                <li>Right to lodge a complaint with your supervisory authority</li>
                <li>Automated decision-making: We use automated systems for fraud detection</li>
                <li>Data transfers: Protected by Standard Contractual Clauses</li>
              </ul>

              <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">13. CCPA Compliance (California Users)</h2>
              <p className="text-gray-700 mb-4">
                California residents have additional rights under the CCPA:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                <li>Right to know what personal information is collected</li>
                <li>Right to delete personal information</li>
                <li>Right to opt-out of sale of personal information (we do not sell your data)</li>
                <li>Right to non-discrimination for exercising CCPA rights</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
